//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ForwardedRef, forwardRef, useMemo } from 'react';
import { Grid, Table as MuiTable } from '@mui/material';
import styled from '@emotion/styled';
import LoadingBar from '@/components/_common/LoadingBar';
import TableHeadSortable from './TableHeadSortable';
import TableBody from './TableBody';
import TableRow from './TableRow';
import {
  selectAssignedUserFilter,
  selectCurrentlySortingBy,
  selectCurrentlySortingOrder,
  selectCurrentTableKey
} from '@/redux/selectors/dataTableSelectors';
import TableDnd from './TableDnd';
import { TableColumn } from '@/redux/slices/slice_types';
import IngestManagementGridItemRender from './IngestManagementGridItemRender';
import { selectUserPreferences } from '@/redux/selectors/authSelectors';
import { useAppSelector } from '@/redux/hooks';

type TableProps = {
  columns: TableColumn[];
  data: any[]; // selected data table data type
  onRowClick?: (e: React.MouseEvent, item?: any) => void | undefined;
  id?: string;
  loading?: boolean;
  onReorderRows?: (taskId: number, priority: number) => Promise<void>;
  onSortColumn?: (id: string) => void;
  activeRowId?: number;
  className?: string;
  scrollable: boolean;
  scrollEnd: 'left' | 'right' | null;
};

const Table = (
  {
    columns = [],
    data = [],
    onRowClick,
    id = '',
    loading = false,
    onReorderRows,
    onSortColumn,
    activeRowId,
    className,
    scrollable,
    scrollEnd
  }: TableProps,
  ref: ForwardedRef<any>
) => {
  const assignedUsersFilter = useAppSelector(selectAssignedUserFilter);
  const sortingByColumn = useAppSelector(selectCurrentlySortingBy);
  const sortingByOrder = useAppSelector(selectCurrentlySortingOrder);

  // current drag and drop condition is if we are filtering by a single user
  // abstract this logic if this ever needs to be reused but foregoing for now bc time
  const dragAndDropCondition = assignedUsersFilter.length === 1;
  const sortingByPriority = Array.isArray(sortingByColumn)
    ? sortingByColumn.includes('priority')
    : sortingByColumn === 'priority' && sortingByOrder === 'asc';
  const dragAndDropEnabled = !!onReorderRows && dragAndDropCondition && sortingByPriority;
  const userPreferences = useAppSelector(selectUserPreferences);
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const gridViewEnabled = useMemo(() => {
    return userPreferences[currentTableKey as keyof typeof userPreferences]?.grid || false;
  }, [userPreferences, currentTableKey]);

  if (dragAndDropEnabled) {
    return (
      <TableDnd
        columns={columns}
        data={data}
        onRowClick={onRowClick}
        onReorderRows={onReorderRows}
        onSortColumn={onSortColumn as any}
        activeRowId={activeRowId}
        scrollable={scrollable}
        scrollEnd={scrollEnd}
      />
    );
  }

  return !gridViewEnabled ? (
    <Wrapper>
      <StyledLoadingBar loading={loading} />
      <StyledTable ref={ref} id={id} loading={`${loading}`} {...{ className }}>
        <TableHeadSortable columns={columns} onSort={onSortColumn} scrollable={scrollable} scrollEnd={scrollEnd} />

        <TableBody>
          {data.map((item, index) => {
            return (
              <TableRow
                disableDrag
                key={`row-${item.id || index}`}
                onRowClick={onRowClick ? (e: React.MouseEvent) => onRowClick(e, item) : undefined}
                columns={columns}
                item={item}
                isActiveRow={!!(activeRowId && item.id === activeRowId)}
                index={index}
                scrollable={scrollable}
                scrollEnd={scrollEnd}
              />
            );
          })}
        </TableBody>
      </StyledTable>
    </Wrapper>
  ) : (
    <Grid container style={{ width: '100%' }}>
      {data.map((item) => (
        <IngestManagementGridItemRender
          key={`row-${item.id}`}
          data={item}
          onGridClick={onRowClick ? (e: React.MouseEvent) => onRowClick(e, item) : undefined}
        />
      ))}
    </Grid>
  );
};

export default forwardRef(Table);

const Wrapper = styled.div`
  position: relative;
`;

const StyledLoadingBar = styled(LoadingBar)`
  position: absolute;
  z-index: 1;
`;

const StyledTable = styled(MuiTable)<{ loading: string }>`
  opacity: ${({ loading }) => (loading === 'true' ? 0.5 : 1)};
  pointer-events: ${({ loading }) => (loading === 'true' ? 'none' : 'auto')};
`;
